<template>
	<div>
		<div class="pa-0 event-description__thumbnail-wrapper">
			<div
				class="event-description__thumbnail-message-wrapper"
				v-if="event.event_sponsor_text"
				v-bind:style="{
					marginBottom: event.event_image ? '0' : '8px',
					borderRadius: event.event_image ? '8px 8px 0 0' : '8px',
				}"
			>
				<marquee>
					{{ event.event_sponsor_text }}
				</marquee>
			</div>
			<div style="position: relative">
				<img
					v-if="!!event.event_image"
					class="event-description__thumbnail-image"
					:src="event.event_image"
					alt="event image"
					loading="lazy"
					v-bind:style="{
						borderRadius: event.event_sponsor_text ? '0 0 8px 8px' : '8px',
					}"
				/>
				<div
					v-if="event.pin_to_top"
					style="top: 10px; right: 10px"
					class="event-preview-card__preview-image-container__pinned"
				>
					<Icon :size="16" type="pinned" />
				</div>
			</div>
		</div>
		<v-card class="event-description__wrapper elevation-0">
			<v-card-text class="event-description__title px-3 pb-0">
				{{ event.event_title }}
			</v-card-text>

			<v-card-text
				class="event-description__date px-3 d-flex align-end"
				v-if="event.dateTimePeriodToString"
			>
				<Icon :size="16" type="union" color="var(--accent-color)" />
				<span class="ml-2">
					{{ event.dateTimePeriodToString }}
				</span>
			</v-card-text>
			<div
				v-if="event.address"
				class="event-description__address-wrapper d-flex align-end px-3"
			>
				<Icon
					v-if="
						event.address.address_city ||
						event.address.address_state ||
						event.address.address_country
					"
					type="pin"
					color="var(--location-color)"
					:size="16"
				/>
				<div class="d-flex ml-2">
					<p class="event-description__address">
						{{ event.address.address_line1 }}
					</p>
					<p class="event-description__address">
						{{ event.address.address_line2 }}
					</p>
					<p class="event-description__address">
						{{ event.address.address_city }}&nbsp;{{ event.address.address_zip }}
					</p>
				</div>
			</div>
			<div class="px-3 mt-3" v-if="event.tags && event.tags.length">
				<v-chip
					v-for="(tag, index) in event.tags"
					:color="tag.tag_bg_color"
					:text-color="tag.tag_txt_color"
					class="post-preview-card__tag mr-2"
					:key="index"
				>
					#{{ tag.tag }}
				</v-chip>
			</div>

			<v-divider class="event-description__divider" />

			<div class="event-description__panel">
				<EventNavbar />
				<div class="event-description__panel-buttons">
					<div>
						<v-btn
							v-if="!isScheduled"
							@click="onSchedule"
							class="event-description__btn-schedule elevation-0"
						>
							Add to My Agenda
						</v-btn>
						<v-btn
							v-else
							class="event-description__btn-schedule-active elevation-0"
							@click="onSchedule"
						>
							Remove
						</v-btn>
					</div>
				</div>
			</div>
			<div v-if="this.event.event_register_link" class="event-preview-card__button-wrapper">
				<a
					class="text-decoration-none"
					style="width: 100%"
					v-if="isExternal && !isFollowed"
					:href="event.event_register_link"
					target="_blank"
				>
					<button type="button" class="event-preview-card__button-wrapper__btn-register">
						Register
					</button>
				</a>
				<button
					type="button"
					v-else-if="!isFollowed"
					class="event-preview-card__button-wrapper__btn-register"
					@click="onFollow"
				>
					Register
				</button>
				<button
					v-else
					type="button"
					class="event-preview-card__button-wrapper__btn-registered"
					@click="onFollow"
				>
					Registered
				</button>
			</div>
		</v-card>
	</div>
</template>

<script>
import { followEvent, unfollowEvent } from '@/api/event'
import { mapActions } from 'vuex'
import pinnedIcon from '@/assets/images/icon-pinned.svg'
import EventNavbar from '@/components/Event/EventNavbar.vue'
import ProfileBadge from '@/components/ProfileBadge.vue'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'EventDescription',
	mixins: [Dwh],
	components: {
		EventNavbar,
		ProfileBadge,
	},
	data() {
		return {
			isScheduled: 0,
			isFollowed: 0,
			pinnedIcon,
		}
	},
	props: {
		event: {
			type: Object,
			default: () => ({}),
		},
	},
	computed: {
		isExternal() {
			return this.event.event_register_link.startsWith('http')
		},
		speakers() {
			return this.event.speakersList
		},
		polls() {
			return this.event.polls.filter(
				(poll) =>
					poll.pollsAnswers.length ||
					(!poll.pollsAnswers.length && poll.allow_custom_answer)
			)
		},
	},
	methods: {
		...mapActions('event', ['addEventToSchedule', 'removeEventFromSchedule']),

		handleRegisterEvent() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_register_button_event',
				module: 'Events',
				data: this.event.event_id,
				title: this.event.event_title,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		onFollow() {
			if (this.isFollowed) {
				unfollowEvent(this.event.event_id)
			} else if (!this.isFollowed) {
				followEvent({ event_id: this.event.event_id })
				this.handleRegisterEvent()
			}

			this.isFollowed = !this.isFollowed
		},
		onSchedule() {
			if (this.isScheduled) {
				this.removeEventFromSchedule(this.event.event_id)
			} else if (!this.isScheduled) {
				this.addEventToSchedule({ event_id: this.event.event_id })
				this.sendTrigger({
					category: 'Content',
					action: 'click_add_to_schedule_event',
					module: 'Events',
					data: this.event.event_id,
					title: this.event.event_title,
					content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
				})
			}
			this.isScheduled = !this.isScheduled
		},
	},
	created() {
		if (this.event) {
			this.isFollowed = this.event.follow
			this.isScheduled = this.event.schedule
		}
	},
}
</script>

<style lang="scss">
.event-description {
	&__wrapper {
		padding: 0 !important;
		background: #fff;
		margin-bottom: 8px;
		border-radius: 8px !important;
		padding-top: 12px !important;
		border: 1px solid #e2e2e2 !important;
	}

	&__title {
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 20px;
		line-height: 22px;
		letter-spacing: 0p;
		color: var(--accent-color) !important;
		margin-bottom: 12px;
	}

	&__date {
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
		color: #4a4a4a;
		margin-bottom: 8px;
	}

	&__address {
		font-family: Inter, sans-serif;
		font-weight: 500;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
		color: var(--location-color);
		margin-bottom: 0 !important;
	}

	&__panel {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		justify-content: space-between;
		padding-right: 16px;
		border-bottom: 1px solid #e2e2e2;

		@media (max-width: 768px) {
			flex-direction: column-reverse;
			padding: 0;
		}

		&-buttons {
			display: flex;
			flex-wrap: nowrap;
			column-gap: 8px;

			@media (max-width: 768px) {
				width: calc(100% - 32px);
				margin: 16px 16px 0 16px;

				& > div {
					flex: 1;
				}
			}

			@media (max-width: 360px) {
				flex-wrap: wrap;
			}
		}
	}

	&__btn-register,
	&__btn-schedule {
		&,
		&-active {
			font-size: 14px;
			font-weight: 700;
			line-height: 17.07px;
			height: 36px !important;
			padding: 0 16px !important;
			text-transform: capitalize;
			border-radius: 8px;

			@media (max-width: 768px) {
				width: 100%;
			}
		}
	}

	&__btn-register {
		background-color: var(--button-secondary-background-color) !important;
		color: var(--button-secondary-color) !important;

		&-active {
			background-color: #9b9b9b !important;
			color: #fff !important;
		}

		&,
		&-active {
			@media (max-width: 360px) {
				margin-bottom: 8px;
			}
		}
	}

	&__btn-schedule {
		background-color: #fff !important;
		color: var(--button-secondary-background-color) !important;
		border: 2px solid var(--button-secondary-background-color);

		&-active {
			color: #9b9b9b !important;
			background-color: #fff !important;
			border: 2px solid #9b9b9b;
		}
	}

	&__thumbnail {
		&-wrapper {
			width: 100%;
			border-radius: 10px;
			position: relative;
			display: flex;
			flex-direction: column;
		}

		&-image {
			width: 100%;
			object-fit: cover;
			max-height: 325px;
		}

		&-message-wrapper {
			overflow: hidden;
			padding: 12px 6px 6px 6px;
			background: #fff;
			border-radius: 10px 10px 0 0;
		}
	}

	&__divider {
		margin-top: 12px;
		border-color: #e2e2e2 !important;
	}
}
</style>
